import React, { useState, useEffect, useMemo } from 'react';
import { Check, ChevronDown, AlertTriangle, CheckCircle, Info, Link2, Unlink } from 'lucide-react';
import {
  ArticleMatch,
  LLMTOCEntry,
  ArticleMappingApproval,
  getConfidenceBadgeColor,
} from '../../services/api';

export interface ArticleMappingReviewProps {
  matches: ArticleMatch[];
  unmatchedBaseline: LLMTOCEntry[];
  unmatchedTarget: LLMTOCEntry[];
  targetArticles: LLMTOCEntry[];
  baselineArticles?: LLMTOCEntry[];
  sourceContractName?: string;
  sourceContractId?: string;
  targetContractName?: string;
  targetContractId?: string;
  onApprove: (approvals: ArticleMappingApproval[]) => void;
  onProceed: (approvals: ArticleMappingApproval[]) => void;
  isLoading?: boolean;
  proceedDisabled?: boolean;
  testId?: string;
}

interface MappingRow {
  baselineNumber: string;
  baselineTitle: string;
  targetNumber: string | null;
  targetTitle: string | null;
  confidence: number;
  matchReason: string;
  approved: boolean;
  isUnmatched: boolean;
}

export const ArticleMappingReview: React.FC<ArticleMappingReviewProps> = ({
  matches,
  unmatchedBaseline,
  unmatchedTarget,
  targetArticles,
  baselineArticles,
  sourceContractName,
  sourceContractId,
  targetContractName,
  targetContractId,
  onApprove,
  onProceed,
  isLoading = false,
  proceedDisabled = false,
  testId = 'article-mapping-review',
}) => {
  // Build the full baseline TOC from matches + unmatchedBaseline (or from passed baselineArticles)
  const fullBaselineTOC: LLMTOCEntry[] = useMemo(() => {
    if (baselineArticles && baselineArticles.length > 0) {
      return [...baselineArticles].sort((a, b) => {
        const numA = parseInt(a.articleNumber.replace(/\D/g, '')) || 0;
        const numB = parseInt(b.articleNumber.replace(/\D/g, '')) || 0;
        return numA - numB;
      });
    }

    // Build from matches + unmatchedBaseline
    const seenBaseline = new Set<string>();
    const baselineArts: LLMTOCEntry[] = [];

    matches.forEach((match) => {
      if (!seenBaseline.has(match.baselineArticleNumber)) {
        seenBaseline.add(match.baselineArticleNumber);
        baselineArts.push({
          articleNumber: match.baselineArticleNumber,
          articleTitle: match.baselineArticleTitle,
          sectionCount: 0,
        });
      }
    });

    unmatchedBaseline.forEach((entry) => {
      if (!seenBaseline.has(entry.articleNumber)) {
        seenBaseline.add(entry.articleNumber);
        baselineArts.push(entry);
      }
    });

    return baselineArts.sort((a, b) => {
      const numA = parseInt(a.articleNumber.replace(/\D/g, '')) || 0;
      const numB = parseInt(b.articleNumber.replace(/\D/g, '')) || 0;
      return numA - numB;
    });
  }, [matches, unmatchedBaseline, baselineArticles]);

  // Initialize mapping rows from matches and unmatched baseline articles
  const initialRows: MappingRow[] = useMemo(() => {
    // Create a map from baseline article number to match
    const matchMap = new Map<string, ArticleMatch>();
    matches.forEach((match) => {
      matchMap.set(match.baselineArticleNumber, match);
    });

    // Build rows for ALL baseline articles
    return fullBaselineTOC.map((baselineEntry) => {
      const match = matchMap.get(baselineEntry.articleNumber);

      if (match) {
        return {
          baselineNumber: match.baselineArticleNumber,
          baselineTitle: match.baselineArticleTitle,
          targetNumber: match.targetArticleNumber,
          targetTitle: match.targetArticleTitle,
          confidence: match.confidence,
          matchReason: match.matchReason,
          approved: match.confidence >= 0.85, // Auto-approve high confidence
          isUnmatched: match.targetArticleNumber === null,
        };
      } else {
        // Baseline article with no match
        return {
          baselineNumber: baselineEntry.articleNumber,
          baselineTitle: baselineEntry.articleTitle,
          targetNumber: null,
          targetTitle: null,
          confidence: 0,
          matchReason: 'No match found in target contract',
          approved: false,
          isUnmatched: true,
        };
      }
    });
  }, [matches, fullBaselineTOC]);

  const [rows, setRows] = useState<MappingRow[]>(initialRows);

  // Update rows when props change
  useEffect(() => {
    setRows(initialRows);
  }, [initialRows]);

  // Build dropdown options for target articles
  const targetOptions = useMemo(() => {
    const options: Array<{ value: string | null; label: string }> = [
      { value: null, label: 'No Match' },
    ];

    targetArticles.forEach((article) => {
      options.push({
        value: article.articleNumber,
        label: `Article ${article.articleNumber}: ${article.articleTitle}`,
      });
    });

    return options;
  }, [targetArticles]);

  // Calculate statistics
  const stats = useMemo(() => {
    const total = rows.length;
    const approved = rows.filter((r) => r.approved).length;
    const highConfidence = rows.filter((r) => r.confidence >= 0.85).length;
    const mediumConfidence = rows.filter((r) => r.confidence >= 0.7 && r.confidence < 0.85).length;
    const lowConfidence = rows.filter((r) => r.confidence > 0 && r.confidence < 0.7).length;
    const unmatched = rows.filter((r) => r.targetNumber === null).length;

    return { total, approved, highConfidence, mediumConfidence, lowConfidence, unmatched };
  }, [rows]);

  const handleTargetChange = (index: number, newTarget: string | null) => {
    setRows((prev) => {
      const updated = [...prev];
      const targetArticle = targetArticles.find((a) => a.articleNumber === newTarget);
      updated[index] = {
        ...updated[index],
        targetNumber: newTarget,
        targetTitle: targetArticle?.articleTitle || null,
        confidence: newTarget ? 0.5 : 0, // Reset confidence when manually changed
        matchReason: newTarget ? 'Manually assigned' : 'No match',
        isUnmatched: newTarget === null,
      };
      return updated;
    });
  };

  const handleApprovalChange = (index: number, approved: boolean) => {
    setRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], approved };
      return updated;
    });
  };

  const handleApproveAllHighConfidence = () => {
    setRows((prev) =>
      prev.map((row) => ({
        ...row,
        approved: row.confidence >= 0.85 ? true : row.approved,
      }))
    );
  };

  const handleSaveApprovals = () => {
    const approvals: ArticleMappingApproval[] = rows.map((row) => ({
      baselineNumber: row.baselineNumber,
      targetNumber: row.targetNumber,
      approved: row.approved,
    }));
    onApprove(approvals);
  };

  const canProceed = !proceedDisabled && rows.some((r) => r.approved && r.targetNumber !== null);

  return (
    <div className="space-y-6" data-testid={testId}>
      {/* Header with stats */}
      <div className="bg-white rounded-lg shadow-md p-6" data-testid={`${testId}-header`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Review Article Mappings</h3>
            <p className="text-sm text-gray-500 mt-1">
              Review and approve AI-suggested article matches before proceeding to section matching
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleApproveAllHighConfidence}
              className="px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
              disabled={isLoading}
              data-testid={`${testId}-approve-high-confidence`}
            >
              <CheckCircle className="h-4 w-4 inline mr-1" />
              Approve All High-Confidence
            </button>
          </div>
        </div>

        {/* Statistics badges */}
        <div className="flex flex-wrap gap-3">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {stats.total} Total Articles
          </span>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {stats.approved} Approved
          </span>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            {stats.highConfidence} High Confidence
          </span>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            {stats.mediumConfidence} Medium Confidence
          </span>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            {stats.lowConfidence} Low Confidence
          </span>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
            {stats.unmatched} Unmatched
          </span>
        </div>
      </div>

      {/* Side-by-side mapping display */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden" data-testid={`${testId}-side-by-side`}>
        {/* Column headers */}
        <div className="grid grid-cols-[1fr_auto_1fr] bg-gray-50 border-b border-gray-200">
          <div className="px-4 py-3">
            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
              Baseline Contract TOC
            </h4>
            {sourceContractName && (
              <p className="text-xs text-gray-500 truncate mt-0.5">{sourceContractName}{sourceContractId ? ` · ${sourceContractId.slice(0, 8)}` : ''}</p>
            )}
            <p className="text-xs text-gray-500 mt-0.5">{fullBaselineTOC.length} articles</p>
          </div>
          <div className="px-4 py-3 flex items-center justify-center bg-gray-100 w-24">
            <span className="text-xs font-medium text-gray-500 uppercase">Match</span>
          </div>
          <div className="px-4 py-3">
            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
              Target Contract Match
            </h4>
            {targetContractName && (
              <p className="text-xs text-gray-500 truncate mt-0.5">{targetContractName}{targetContractId ? ` · ${targetContractId.slice(0, 8)}` : ''}</p>
            )}
            <p className="text-xs text-gray-500 mt-0.5">Arranged by baseline match</p>
          </div>
        </div>

        {/* Mapping rows */}
        <div className="divide-y divide-gray-100">
          {rows.map((row, index) => (
            <div
              key={row.baselineNumber}
              className={`grid grid-cols-[1fr_auto_1fr] ${
                row.approved ? 'bg-green-50' : ''
              } hover:bg-gray-50 transition-colors`}
              data-testid={`${testId}-row-${index}`}
            >
              {/* Baseline article (left column) */}
              <div className="px-4 py-3 border-r border-gray-100">
                <div className="flex items-start gap-3">
                  <label className="flex items-center cursor-pointer mt-0.5">
                    <input
                      type="checkbox"
                      checked={row.approved}
                      onChange={(e) => handleApprovalChange(index, e.target.checked)}
                      disabled={isLoading}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      data-testid={`${testId}-approve-${index}`}
                    />
                  </label>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-800">
                        Art. {row.baselineNumber}
                      </span>
                    </div>
                    <div className="text-sm text-gray-900 mt-1" title={row.baselineTitle}>
                      {row.baselineTitle}
                    </div>
                  </div>
                </div>
              </div>

              {/* Connection indicator (center column) */}
              <div className="px-2 py-3 flex flex-col items-center justify-center bg-gray-50 w-24">
                {row.targetNumber !== null ? (
                  <>
                    <Link2 className="h-5 w-5 text-primary-500" />
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${getConfidenceBadgeColor(row.confidence)}`}
                      data-testid={`${testId}-confidence-${index}`}
                    >
                      {Math.round(row.confidence * 100)}%
                    </span>
                  </>
                ) : (
                  <>
                    <Unlink className="h-5 w-5 text-gray-300" />
                    <span className="text-xs text-gray-400 mt-1">No match</span>
                  </>
                )}
              </div>

              {/* Target match (right column) */}
              <div className="px-4 py-3 border-l border-gray-100">
                {row.targetNumber !== null ? (
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-800">
                        Art. {row.targetNumber}
                      </span>
                      {row.targetNumber !== row.baselineNumber && (
                        <span className="text-xs text-amber-600 font-medium">
                          (reordered)
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-900 mt-1" title={row.targetTitle || ''}>
                      {row.targetTitle}
                    </div>
                    <div className="flex items-center mt-2 text-xs text-gray-500">
                      <Info className="h-3 w-3 mr-1" />
                      <span className="truncate" title={row.matchReason}>{row.matchReason}</span>
                    </div>
                  </div>
                ) : (
                  <div className="min-w-0">
                    <div className="relative">
                      <select
                        value={row.targetNumber || ''}
                        onChange={(e) => handleTargetChange(index, e.target.value || null)}
                        disabled={isLoading}
                        className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 rounded-md bg-white"
                        data-testid={`${testId}-target-select-${index}`}
                      >
                        {targetOptions.map((option) => (
                          <option key={option.value || 'null'} value={option.value || ''}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    </div>
                    <div className="flex items-center mt-2 text-xs text-yellow-600">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      <span>{row.matchReason}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Unmatched target articles info */}
      {unmatchedTarget.length > 0 && (
        <div
          className="bg-yellow-50 border border-yellow-200 rounded-lg p-4"
          data-testid={`${testId}-unmatched-target`}
        >
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-yellow-800">
                {unmatchedTarget.length} Target Article{unmatchedTarget.length !== 1 ? 's' : ''} Not
                Matched
              </h4>
              <p className="text-sm text-yellow-700 mt-1">
                The following articles in the target contract have no matching baseline article:
              </p>
              <ul className="mt-2 text-sm text-yellow-700 list-disc list-inside">
                {unmatchedTarget.slice(0, 5).map((entry) => (
                  <li key={entry.articleNumber}>
                    Article {entry.articleNumber}: {entry.articleTitle}
                  </li>
                ))}
                {unmatchedTarget.length > 5 && (
                  <li>...and {unmatchedTarget.length - 5} more</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex justify-between items-center" data-testid={`${testId}-actions`}>
        <div className="text-sm text-gray-500">
          {stats.approved} of {stats.total} articles approved for section matching
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleSaveApprovals}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isLoading}
            data-testid={`${testId}-save`}
          >
            Save Approvals
          </button>
          <button
            type="button"
            onClick={() => {
              const approvals: ArticleMappingApproval[] = rows.map((row) => ({
                baselineNumber: row.baselineNumber,
                targetNumber: row.targetNumber,
                approved: row.approved,
              }));
              onProceed(approvals);
            }}
            disabled={!canProceed || isLoading}
            className={`px-6 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
              canProceed && !isLoading
                ? 'bg-primary-600 hover:bg-primary-700'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
            data-testid={`${testId}-proceed`}
          >
            <Check className="h-4 w-4 inline mr-1" />
            Proceed to Section Matching
          </button>
        </div>
      </div>
    </div>
  );
};

export default ArticleMappingReview;
